/*******************************************************************************
  inspectionDataProcessor.js

  Author: Advait
*******************************************************************************/
export function processInspectionData(data) {
  if (!data || !Array.isArray(data)) return [];

  return data.map((item) => {
    // Find inspection and snapshot objects
    const inspectionObj = item.inspectiondata?.find((data) => data.type === "INSPECTION");
    const snapshotObj = item.inspectiondata?.find((data) => data.type === "SNAPSHOT");

    // Find inspection and snapshot objects
    const inspectionDetails = {};

    // Wi-Fi data
    const wifi_data = item.inspectiondata?.find((data) => data.type === "WIFISCAN") || null;
    const ssid = wifi_data?.value?.current_status?.ap?.ssid ?? null;
    const strength = getSignalStrengthFromScanResults(wifi_data?.value);
    if (wifi_data !== null && strength !== null)
      inspectionDetails[`Wi-Fi (${sanitizeSSID(ssid)})`] = `${strength} dBm`;

    // Sound data
    const sound_data =
      item.inspectiondata?.find((data) => data.type === "SOUNDENVIRONMENT") || null;
    const ambient_noise = sound_data?.value?.ambient_noise ?? null;
    if (ambient_noise !== null) inspectionDetails[`Ambient Noise`] = `${ambient_noise} dB(A)`;
    const sound_level = sound_data?.value?.sound_level ?? null;
    if (sound_level !== null) inspectionDetails[`Sound Level`] = `${sound_level} dB(A)`;

    // Display data
    const display = item.inspectiondata?.find((data) => data.type === "DISPLAY") || null;
    if (display !== null) {
      inspectionDetails[`Display`] = `${formatInspectionDataValue(display.value)}`;
    }

    // Door data
    const door = item.inspectiondata?.find((data) => data.type === "DOOR") || null;
    if (door !== null) {
      inspectionDetails[`Door`] = `${formatInspectionDataValue(door.value)}`;
    }

    return {
      name: formatName(item.maptag),
      time: formatTime(item.sitetime),
      url: snapshotObj ? snapshotObj.value : null,
      inspectionValue: inspectionObj ? inspectionObj.value : null,
      inspection_details: inspectionDetails,
      coords: inspectionObj ? inspectionObj.location : null,
    };
  });
}
/**
 * Format location name
 * check if name have # in it,
 * if it does, strip off the part till # from name
 */
export function formatName(name) {
  if (name && name.includes("#")) return name.split("#")[1];
  return name;
}
/**
 * Format time from 2025-04-15 07:02:30 to Apr-15-2025 7:02 a.m.
 */
export function formatTime(siteTime) {
  if (!siteTime) return "";
  const date = new Date(siteTime);
  return date
    .toLocaleString("en-US", {
      month: "short",
      day: "2-digit",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    })
    .replace(/,/g, "")
    .replace(/(\w+) (\d+) (\d+)/, "$1-$2-$3")
    .replace(/(AM|PM)/, (match) => (match === "AM" ? "a.m." : "p.m."));
}
/**
 * Format inspection data values to make first letter Uppercase
 */
export function formatInspectionDataValue(value) {
  return value.charAt(0).toUpperCase() + value.slice(1);
}

/**
 * Sanitize the SSID name to make sure ot fits in inspection details box
 * If the SSID name exceeds 10 chars, it replaces ssid name by taking
 * first 11 chars and then append ... to the end.
 * For ex 'MyWiFiNetwork123' will be updated to 'MyWiFiN...'
 */
export function sanitizeSSID(ssid) {
  if (ssid.length > 10) {
    return ssid.substring(0, 7) + "...";
  }
  return ssid;
}
/**
 * get the signal strength from scanned value for current ssid
 * @param {*} wifiData
 * @returns signal value / null
 */
function getSignalStrengthFromScanResults(wifiData) {
  const currentSsidHex = wifiData?.current_status?.ap?.ssid_hex;
  if (!currentSsidHex) {
    return null; // No current SSID found
  }

  // Find matching entry in scan_results and return the signal value
  const matchingResult = wifiData?.scan_results?.find(
    (result) => result.ssid_hex === currentSsidHex
  );
  return matchingResult ? matchingResult.signal : null;
}
